// @ts-strict-ignore
import { FloatingServiceMenu, Footer, Header, Page, PageTransition } from '@components/Page'
import { pickSeoProps } from '@components/Page/Page'
import ContentBlocks from '@components/_content-page/ContentBlocks'
import { buildLanguageAlternateSlugItems } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { getDataCodenameValues } from '@lib/getDataCodenameValues'
import {
  MultipleItemResponse,
  SingleItemResponse,
  fetchContent,
  getPageByUrlSlug,
} from '@lib/kontent'
import { NewsItem as NewsItemModel } from '@lib/kontent/models/NewsItem'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { FC } from 'react'

type Props = SingleItemResponse<NewsItemModel>

const NewsItem: FC<Props> = ({ item, modular_content }) => {
  const { locales } = useRouter()
  const contentBlocks = getDataCodenameValues(item.elements.content_blocks.value).map((key) => ({
    type: modular_content[key].system.type,
    elements: modular_content[key].elements,
  }))
  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateSlugItems(
        locales,
        'newsPage',
        null,
        `/${item.elements.url_slug.value}`,
      )}
    >
      <FloatingServiceMenu />
      <Header initialVariant="absolute" />
      <PageTransition>
        <ContentBlocks contentBlocks={contentBlocks} modularContent={modular_content} />
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default NewsItem

export const getStaticProps: GetStaticProps = async ({ locale, params, preview }) => {
  const urlSlug = typeof params.slug === 'string' ? params.slug : params.slug.join('/')

  const content = await getPageByUrlSlug<NewsItemModel>({
    language: locale,
    urlSlug,
    preview,
  })

  if (!content) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...content,
      ...(await getServerSideTranslations(locale)),
    },
  }
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (process.env.VERCEL_ENV !== 'production') {
    return { paths: [], fallback: 'blocking' }
  }

  let paths = []
  for (const locale of locales) {
    const newsItemPages: MultipleItemResponse<NewsItemModel> = await fetchContent({
      path: '',
      params: {
        elements: ['url_slug'],
        'system.type[in]': ['news_item'],
        language: locale,
      },
    })
    newsItemPages.items.forEach((item) => {
      if (item.system.language === locale) {
        paths.push({
          params: {
            slug: [item.elements.url_slug.value],
          },
          locale,
        })
      }
    })
  }

  return { paths, fallback: false }
}
